import { serializeError } from 'utils'
import { createTableSlice } from 'features/dashboard/table/createTableSlice'
import { CampaignTypes, DashboardTypes } from 'features/Codes'
import api from 'api/axios/foka'
import { filterMetricColumns } from 'features/dashboard/utils/dashboardUtils'
import { createAsyncThunk } from '@reduxjs/toolkit'
import { createAxiosThunk } from 'utils/reduxUtil'

export const fetchKeywordSummary = createAsyncThunk(
  'dashboard/fetchKeywordSummary',
  async ({ from, to, columns, campaignIds, adGroupIds, filters }) => {
    const [summary, extraSummary] = await Promise.all([
      api.dashboard
        .getKeywordTableReportTotal({
          from,
          to,
          columns,
          campaignIds,
          adGroupIds,
          filters,
        })
        .then(res => res?.data?.reportResponse?.data[0] || {})
        .catch(() => ({})),
      api.dashboard
        .getKeywordTableReportMediaType({
          from,
          to,
          columns,
          campaignIds,
          adGroupIds,
          filters,
        })
        .then(res => res?.data?.reportResponse?.data[0] || {})
        .catch(() => ({})),
    ])
    return {
      summary,
      extraSummary,
    }
  }
)

export const fetchKeywordTable = createAsyncThunk(
  'dashboard/fetchKeywordTable',
  async (noArgs, { getState, rejectWithValue, dispatch }) => {
    let error
    const {
      common: { isHeavyUser, from, to },
      filter: { filters },
      table: {
        config: {
          columns: { [DashboardTypes.KEYWORD]: columns },
        },
        campaignTable: { selectedIds: campaignIds },
        adGroupTable: { selectedIds: adGroupIds },
        keywordTable: { page, sort },
      },
    } = getState().dashboard
    if (isHeavyUser && !(campaignIds.length === 1 || adGroupIds.length > 0)) {
      return {
        content: [],
        totalElements: 0,
      }
    }
    const metricColumns = filterMetricColumns(columns)
    if (metricColumns.length > 0) {
      dispatch(
        fetchKeywordSummary({
          from,
          to,
          columns: metricColumns,
          campaignIds,
          adGroupIds,
          filters,
        })
      )
    }
    const { content, totalElements } = await api.dashboard
      .getKeywordTable({
        from,
        to,
        page,
        sort,
        campaignIds,
        adGroupIds,
        filters,
      })
      .then(res => res?.data?.result)
      .catch(err => {
        error = serializeError(err)
        return {
          content: [],
          totalElements: 0,
        }
      })
    if (error) {
      return rejectWithValue(error)
    } else {
      let metricError
      const ids = content.map(({ id }) => id)
      const premiumLinkIds = content
        .filter(({ keywordType }) => keywordType === CampaignTypes.PREMIUM_LINK)
        .map(({ id }) => id)
      if (premiumLinkIds.length > 0 && columns.includes('score')) {
        dispatch(fetchKeywordsQi(premiumLinkIds))
      }
      const metrics =
        totalElements === 0 || metricColumns.length === 0
          ? {}
          : await api.dashboard
              .getKeywordTableReport({
                from,
                to,
                columns: metricColumns,
                ids,
              })
              .then(res => res?.data?.metrics)
              .catch(() => {
                metricError = '지표 데이터를 불러오지 못했습니다.'
                return {}
              })
      return {
        totalElements,
        content: content.map(item => ({
          ...item,
          ...metrics[item.id],
        })),
        error: metricError,
      }
    }
  }
)

export const fetchKeywordsQi = createAxiosThunk(
  'dashboard/fetchKeywordsQi',
  ids => api.dashboard.getKeywordsQi(ids)
)

export const KeywordTableEditTypes = {
  maxBiddingAmount: 'maxBiddingAmount',
  landingUrl: 'landingUrl',
}

const keywordTableSlice = createTableSlice({
  type: DashboardTypes.KEYWORD,
  initialState: {
    extraSummary: {},
    qis: {},
    qiLoading: false,
    qiError: undefined,
    editable: false,
    editType: undefined,
  },
  fetcher: fetchKeywordTable,
  summaryFetcher: fetchKeywordSummary,
  reducers: {
    clearQiError: state => {
      state.qiError = undefined
    },
    setEditType: (state, { payload }) => {
      state.editType = payload
      state.editable = true
    },
    exitEditable: state => {
      state.editable = false
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchKeywordsQi.pending, state => {
      state.qiLoading = true
    })

    builder.addCase(
      fetchKeywordsQi.fulfilled,
      (state, { payload: { results } }) => {
        state.qis = results.reduce(
          (obj, { id, index }) => ({
            ...obj,
            [id]: index,
          }),
          {}
        )
        state.qiLoading = false
      }
    )

    builder.addCase(fetchKeywordsQi.rejected, (state, { payload, error }) => {
      state.qiError = payload || error
      state.qiLoading = false
    })
  },
})

const { actions, reducer } = keywordTableSlice
export const keywordTableActions = actions
export const {
  reset,
  toggleSelectedId,
  setSelectedIds,
  setPage,
  setSort,
  clearError,
  setStatus,
  clearQiError,
  setEditType,
  exitEditable,
} = actions
export default reducer
